import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { getLanguageByCode } from '../../../constants';
import { getTranscript } from '../../../services/youtube';
import { Capacitor } from '@capacitor/core';
import { performPreAnalysisCheck } from '../../../utils/preAnalysisCheck';

export const useFirestoreCreator = () => {

  // 새 히스토리 생성 (사전 체크 후 진행)
  const createNewHistory = async (inputUrl, extractedVideoId, videoTitle, userId, language, setInputUrl, setInputTitle) => {
    try {
      // 🔍 Step 0: 사전 체크 (자막 존재 여부 + 실시간 방송 감지)
      console.log('🔍 [Step 0] 사전 체크 시작');
      
      try {
        const preCheckResult = await performPreAnalysisCheck(extractedVideoId, inputUrl);
        console.log('✅ [Step 0] 사전 체크 완료:', preCheckResult);
      } catch (preCheckError) {
        console.error('❌ [Step 0] 사전 체크 실패:', preCheckError);
        
        // 사전 체크 실패 시 즉시 에러 던지기 (히스토리 생성하지 않음)
        throw preCheckError;
      }
      
      const isNativePlatform = Capacitor.isNativePlatform();
      const platform = Capacitor.getPlatform();
      
      console.log('🔍 [플랫폼 확인]:', { isNativePlatform, platform });
      
      // 🔥 Step 1: 플랫폼별 자막 추출 전략 (단순화)
      let transcript = "";
      let transcriptSource = "";
      let extractionMethod = "";
      let clientSideExtraction = false;
      
      // 🔥 자막 추출 시 sourceLanguage 추출
      let sourceLanguage = null;
      
      if (isNativePlatform) {
        // 📱 네이티브 앱: 네이티브 추출 시도 → 서버 폴백
        console.log('📱 [네이티브 앱] 자막 추출 시작:', { videoId: extractedVideoId, platform });
        
        try {
          const transcriptResult = await getTranscript(inputUrl, userId, null);
          transcript = transcriptResult.transcript;
          transcriptSource = transcriptResult.source; // 'native' or 'server' 
          extractionMethod = transcriptResult.method; 
          clientSideExtraction = transcriptResult.source === 'native';
          sourceLanguage = transcriptResult.sourceLanguage; // 🔥 원본 언어 정보 추출
          
          console.log('✅ [네이티브 앱] 자막 추출 완료:', { 
            transcriptLength: transcript.length,
            source: transcriptSource,
            method: extractionMethod,
            clientSideExtraction: clientSideExtraction,
            sourceLanguage: sourceLanguage,
            platform: transcriptResult.platform || platform
          });
          
        } catch (transcriptError) {
          console.error('❌ [네이티브 앱] 자막 추출 실패:', transcriptError);
          // 네이티브에서 자막 추출 실패 시 서버에서 처리하도록 빈 값으로 설정
          transcript = "";
          transcriptSource = "extraction_failed";
          extractionMethod = "extraction_failed";
          clientSideExtraction = false;
          sourceLanguage = null;
        }
      } else {
        // 🌐 웹앱: 서버 방식만 사용
        console.log('🌐 [웹앱] 서버 방식으로 자막 추출');
        try {
          const transcriptResult = await getTranscript(inputUrl, userId, null);
          transcript = transcriptResult.transcript;
          transcriptSource = transcriptResult.source; // 'server'
          extractionMethod = transcriptResult.method; // 'SERVER_ONLY'
          clientSideExtraction = false; // 웹에서는 항상 false
          sourceLanguage = transcriptResult.sourceLanguage; // 🔥 원본 언어 정보 추출
          
          console.log('✅ [웹앱] 서버 자막 추출 완료:', { 
            transcriptLength: transcript.length,
            source: transcriptSource,
            method: extractionMethod,
            sourceLanguage: sourceLanguage
          });
        } catch (transcriptError) {
          console.error('❌ [웹앱] 서버 자막 추출 실패:', transcriptError);
          transcript = "";
          transcriptSource = "server_failed";
          extractionMethod = "server_failed";
          clientSideExtraction = false;
          sourceLanguage = null;
        }
      }
      
      // 6개 언어 지원 - 현재 언어 정보 가져오기
      const currentLanguage = getLanguageByCode(language);
      const languageNames = {
        'ko': '한국어',
        'en': 'English', 
        'zh': '中文',
        'ja': '日本語',
        'es': 'Español',
        'fr': 'Français'
      };
      
      const newVideo = {
        url: inputUrl.trim(),
        videoId: extractedVideoId,
        title: videoTitle,
        youtubeTitle: videoTitle,
        customTitle: videoTitle,
        summary: "",
        chunk_summaries: [],
        chapters: [],
        transcript: transcript, // 플랫폼별 자막 추출 결과
        subtitle: transcript,   // 레거시 호환성
        status: 'analyzing', // 자막 있든 없든 분석 상태로 시작
        userId: userId,
        lastUpdated: serverTimestamp(),
        createdAt: serverTimestamp(),
        qa: [],
        questions: [],
        projectId: null,
        language: language,
        languageName: languageNames[language] || currentLanguage?.nativeName || 'Unknown',
        sourceLanguage: sourceLanguage, // 🔥 원본 언어 정보 저장
        // 🔥 자막 추출 관련 메타데이터 (단순화)
        transcriptSource: transcriptSource, // 'native', 'server', 'extraction_failed', 'server_failed'
        extractionMethod: extractionMethod, 
        clientSideExtraction: clientSideExtraction, // 네이티브 추출 성공 여부
        extractedAt: serverTimestamp(),
        // 🔍 사전 체크 완료 표시
        preValidated: true,
        preValidatedAt: serverTimestamp()
      };
      
      console.log('📝 [Firestore] 히스토리 생성 중...', {
        videoId: extractedVideoId,
        platform: platform,
        isNativePlatform: isNativePlatform,
        transcriptLength: transcript.length,
        source: transcriptSource,
        clientSideExtraction: clientSideExtraction,
        status: newVideo.status,
        preValidated: true
      });
      
      const docRef = await addDoc(collection(db, "users", userId, "history"), newVideo);
      const docId = docRef.id;
      
      console.log('✅ [Firestore] 히스토리 생성 완료:', {
        historyId: docId,
        platform: platform,
        transcriptIncluded: transcript.length > 0,
        clientSideExtraction: clientSideExtraction,
        needsServerProcessing: transcript.length === 0,
        preValidated: true
      });
      
      // 입력 필드 초기화
      setInputUrl("");
      setInputTitle("");
      
      // 생성된 historyId 반환 (페이지 이동용)
      return { 
        historyId: docId,
        hasTranscript: transcript.length > 0,
        extractionMethod: extractionMethod,
        transcriptSource: transcriptSource,
        clientSideExtraction: clientSideExtraction,
        platform: platform,
        preValidated: true
      };
    } catch (error) {
      console.error('🚫 [Firestore 저장 에러]:', error);
      throw error;
    }
  };

  return {
    createNewHistory
  };
}; 